/*
  Bundle gift — "Buy 4, get the 5th at a discount"
  ==========================================================================
  Three custom elements share one API client:

    <bundle-gift-offers>  home page   — GET  /active   every live bundle
    <bundle-gift-family>  product page— GET  /family   "complete the set" rail
    <bundle-gift-cart>    cart        — POST /check    progress + claim
                                        POST /claim    mints the code

  Conventions of this backend (see the integration brief):
    - Business failures are HTTP 200 with success:false and an error code. Only
      real 4xx/5xx are errors. A shopper who isn't eligible is a normal state.
    - platform must always be "web": a query param on GETs, a body field on POSTs.
    - Product ids come back as bare numeric strings; handle is what we key off.
    - Unset text fields are "" — never null.
    - Prices are major units (3, 6, 18), not cents, and are authoritative:
      anything the client sends as a price is ignored server-side.

  Caching: /family is cached per product for the session. /check and /claim are
  never cached.

  Everything fails open. If the API is unreachable the rails and the cart strip
  simply stay hidden — none of this is on the critical purchase path.
*/
(function () {
  if (window.LGBundleGift) return;

  var DEFAULT_BASE = 'https://livgood-pt.herokuapp.com/api/v2/product-group-gift';

  var STORAGE = {
    device: 'lg_bg_device_id',
    email: 'lg_bg_email',
    claim: 'lg_bg_claim_v1',
    family: 'lg_bg_family_',
    variant: 'lg_bg_variant_'
  };

  /* ---------------------------------------------------------------- storage */

  function readLocal(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }

  function writeLocal(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function removeLocal(key) {
    try { window.localStorage.removeItem(key); } catch (e) { /* ignore */ }
  }

  function deviceId() {
    var id = readLocal(STORAGE.device);
    if (!id) {
      id = 'web-session-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
      writeLocal(STORAGE.device, id);
    }
    return id;
  }

  /* The claim we already hold, if it hasn't expired yet. */
  function readClaim() {
    var raw = readLocal(STORAGE.claim);
    if (!raw) return null;
    try {
      var claim = JSON.parse(raw);
      if (!claim || !claim.code) return null;
      if (claim.expiresAt && new Date(claim.expiresAt).getTime() <= Date.now()) {
        removeLocal(STORAGE.claim);
        return null;
      }
      return claim;
    } catch (e) {
      removeLocal(STORAGE.claim);
      return null;
    }
  }

  function writeClaim(claim) {
    writeLocal(STORAGE.claim, JSON.stringify(claim));
  }

  function clearClaim() {
    removeLocal(STORAGE.claim);
  }

  /* ------------------------------------------------------------------- money */

  function toNumber(value) {
    if (value == null || value === '') return 0;
    var n = parseFloat(String(value).replace(',', '.'));
    return isNaN(n) ? 0 : n;
  }

  /* Amounts from this API are major units, so convert to cents to reuse the
     shop's money format string. */
  function money(value) {
    var format = (window.KROWN && KROWN.settings && KROWN.settings.shop_money_format) || '${{amount}}';
    format = String(format).replace(/<[^>]*>/g, '').trim();
    var cents = Math.round(toNumber(value) * 100);

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      var fixed = (number / 100).toFixed(precision);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var token = match ? match[1] : 'amount';
    var out;
    switch (token) {
      case 'amount_no_decimals': out = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': out = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelimiters(cents, 0, '.'); break;
      case 'amount_with_space_separator': out = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': out = withDelimiters(cents, 2, "'", '.'); break;
      default: out = withDelimiters(cents, 2);
    }
    return format.replace(placeholder, out);
  }

  function sized(src, width) {
    if (!src) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  /* -------------------------------------------------------------- API client */

  function apiGet(base, path, params) {
    var query = ['platform=web'];
    Object.keys(params || {}).forEach(function (key) {
      var value = params[key];
      if (value !== undefined && value !== null && value !== '') {
        query.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
      }
    });
    return fetch(base + path + '?' + query.join('&'), { headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error(path + ' ' + res.status);
        return res.json();
      });
  }

  function apiPost(base, path, body) {
    var payload = Object.assign({ platform: 'web' }, body || {});
    return fetch(base + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) throw new Error(path + ' ' + res.status);
      return res.json();
    });
  }

  /* -------------------------------------------------------------------- cart */

  /*
    The cart as both features need it:
      items — lines collapsed to { handle, quantity }, the shape /check and
              /claim want, with variants of the same product summed
      total — the cart subtotal in major units, which is what a Shopify
              discount's minimum-order threshold is actually measured against
  */
  function cartSnapshot() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('cart.js ' + res.status);
        return res.json();
      })
      .then(function (cart) {
        var byHandle = {};
        (cart.items || []).forEach(function (line) {
          if (!line.handle) return;
          byHandle[line.handle] = (byHandle[line.handle] || 0) + (line.quantity || 0);
        });
        return {
          items: Object.keys(byHandle).map(function (handle) {
            return { handle: handle, quantity: byHandle[handle] };
          }),
          // /cart.js reports cents; every amount in this API is major units.
          total: (cart.total_price || 0) / 100
        };
      });
  }

  /* The cart holds at least one full set, whatever eligible says. */
  function setComplete(progress) {
    return progress && progress.remainingToUnlock != null &&
      toNumber(progress.remainingToUnlock) <= 0;
  }

  function cartItems() {
    return cartSnapshot().then(function (snapshot) { return snapshot.items; });
  }

  /*
    How many reward units a code covers: one per complete set in the cart
    (9 qualifying items in a group that unlocks at 3 -> 3). Both /check and
    /claim carry it. An older backend without the field means one.
  */
  function rewardUnits(response) {
    var units = Math.floor(toNumber(response && response.rewardUnits));
    return units > 0 ? units : 1;
  }

  /*
    First available variant of a product, so a handle can be added to the cart.

    Cached per handle for the session, and prefetched on hover, because
    /products/<handle>.js costs about a second on this store — long enough that
    doing it inside the click would dominate the time to add.
  */
  var productPromises = {};

  /* One /products/<handle>.js fetch per handle per page, shared by variant
     resolution and price display. Resolves to { variantId, price } with price
     in major units, straight from Shopify — the API's price field is not
     trusted for display because it is often unset (renders as $0.00). */
  function shopifyProduct(handle) {
    if (productPromises[handle]) return productPromises[handle];

    var variantKey = STORAGE.variant + handle;

    productPromises[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('product ' + res.status);
        return res.json();
      })
      .then(function (product) {
        var variants = product.variants || [];
        var available = variants.filter(function (v) { return v.available; })[0];
        var variant = available || variants[0] || {};
        var id = variant.id || null;
        // Shopify's .js endpoints report cents; everything else here is major units.
        var price = variant.price != null ? variant.price / 100 : null;
        if (id) writeLocal(variantKey, String(id));
        // `available` is real stock: false when every variant is sold out.
        return { variantId: id, price: price, available: !!available };
      })
      .catch(function (e) {
        delete productPromises[handle]; // let a later click retry
        throw e;
      });

    return productPromises[handle];
  }

  function firstAvailableVariant(handle) {
    return shopifyProduct(handle).then(function (info) {
      return info.available ? info.variantId : null;
    });
  }

  /* Whether the product has any variant in stock, from Shopify itself. */
  function productInStock(handle) {
    return shopifyProduct(handle).then(function (info) { return info.available; });
  }

  /* Fills `node` with the product's real Shopify price. `fallback` is the
     API's price, used only if the Shopify lookup fails and it is a real
     number above zero — never to show $0.00. */
  function fillShopifyPrice(handle, node, fallback) {
    var useFallback = function () {
      var value = toNumber(fallback);
      if (value > 0) node.textContent = money(value);
    };
    if (!handle) { useFallback(); return; }
    shopifyProduct(handle).then(function (info) {
      if (info.price != null) {
        node.textContent = money(info.price);
      } else {
        useFallback();
      }
    }).catch(useFallback);
  }

  function addToCart(variantId, quantity) {
    var url = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_add_url) || '/cart/add';
    return fetch(url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart/add ' + res.status);
      return res.json();
    });
  }

  /* Re-render the theme's own cart markup, the same way component-cart.js does
     after a quantity change, so a gift added in the background shows up. */
  function refreshCartUI() {
    return fetch('?section_id=helper-cart')
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var form = doc.getElementById('AjaxCartForm');
        var subtotal = doc.getElementById('AjaxCartSubtotal');

        var targetForm = document.getElementById('AjaxCartForm');
        if (targetForm && form) {
          targetForm.innerHTML = form.innerHTML;
          if (typeof targetForm.ajaxifyCartItems === 'function') targetForm.ajaxifyCartItems();
        }
        var targetSubtotal = document.getElementById('AjaxCartSubtotal');
        if (targetSubtotal && subtotal) targetSubtotal.innerHTML = subtotal.innerHTML;

        if (form) {
          var count = form.querySelector('[data-cart-count]');
          var total = form.querySelector('[data-cart-total]');
          if (count) {
            document.querySelectorAll('[data-header-cart-count]').forEach(function (elm) {
              elm.textContent = count.textContent;
            });
          }
          if (total) {
            document.querySelectorAll('[data-header-cart-total]').forEach(function (elm) {
              elm.textContent = total.textContent;
            });
          }
        }
      })
      .catch(function () { /* cosmetic only — never block the claim */ });
  }

  /* ------------------------------------------------------------ cart watcher */

  /*
    The theme mutates the cart from several places (product form, cart form,
    quick buy) and none of them dispatch a document-level event. Wrapping fetch
    is the one hook that catches all of them, including any app that writes to
    the cart the same way.
  */
  var CART_ROUTES = /\/cart\/(add|change|update|clear)/;

  function installCartWatcher() {
    if (window.__lgBundleCartWatcher) return;
    window.__lgBundleCartWatcher = true;

    var nativeFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = nativeFetch.apply(this, arguments);
      if (CART_ROUTES.test(url)) {
        result.then(function () {
          document.dispatchEvent(new CustomEvent('lg:cart:updated'));
        }).catch(function () { /* the caller handles its own failure */ });
      }
      return result;
    };

    // Cart mutations that happen in another tab, or a bfcache restore.
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) document.dispatchEvent(new CustomEvent('lg:cart:updated'));
    });
  }

  /* ------------------------------------------------------------------ shared */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* "Add {{remaining}} more to unlock your gift" -> filled in. */
  function template(str, values) {
    return String(str || '').replace(/\{\{\s*(\w+)\s*\}\}/g, function (match, key) {
      return values[key] != null ? values[key] : match;
    });
  }

  /*
    A product card for a bundle rail.

    The card is a container rather than one big <a>, because it carries an add
    button and a button nested inside an anchor is invalid markup that steals
    the click. The image, title and price sit in an inner link; the button is
    its sibling.

    opts.onAdd turns the button on. Everything about adding — resolving a
    variant, posting to the cart, re-rendering the theme's cart markup — lives
    in addProductToCart() so both rails behave identically.
  */
  /*
    A product card for a bundle rail.

    The whole card is the control: clicking anywhere on it puts that product in
    the cart. Completing a set is a picking exercise, so every card should be
    one target rather than a small button inside a card that also happens to be
    a link somewhere else.

    That means the card is a <button>, not an <a>. The product page is still
    reachable through a small "View" link underneath, which stops its own click
    from bubbling up and adding to the cart.

    Adding is never destructive: clicking an already-picked card adds another
    rather than removing it. Taking things out belongs to the cart, where the
    shopper can see what they are removing.
  */
  function productCard(product, options) {
    var opts = options || {};
    var available = product.available !== false;
    var handle = product.handle || '';
    var pickable = !!(opts.onAdd && available && handle);

    var card = el(pickable ? 'button' : 'div', 'bg-card element--border-radius');
    if (pickable) {
      card.type = 'button';
      card.classList.add('bg-card--pickable');
      card.setAttribute('aria-label', (opts.addLabel || 'Add') + ' ' + (product.title || ''));
    }
    if (!available) card.classList.add('bg-card--unavailable');
    if (opts.isReward) card.classList.add('bg-card--reward');
    if (handle) card.dataset.handle = handle;

    var imageWrap = el('span', 'bg-card__image');
    if (product.image) {
      var img = document.createElement('img');
      img.src = sized(product.image, 400);
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      imageWrap.appendChild(img);
    }
    if (!available) {
      imageWrap.appendChild(el('span', 'bg-card__flag', opts.unavailableLabel || 'Unavailable'));
    } else if (opts.isReward) {
      imageWrap.appendChild(el('span', 'bg-card__flag bg-card__flag--reward', opts.rewardLabel || 'Your gift'));
    }
    imageWrap.appendChild(el('span', 'bg-card__tick', '✓'));
    if (pickable) {
      imageWrap.appendChild(el('span', 'bg-card__hint', opts.addLabel || 'Add'));
    }
    card.appendChild(imageWrap);

    card.appendChild(el('span', 'bg-card__title', product.title || ''));
    var price = el('span', 'bg-card__price', '');
    card.appendChild(price);
    fillShopifyPrice(handle, price, product.price);

    // Status line: doubles as the "Adding…" / "Added · N" feedback slot.
    var status = el('span', 'bg-card__status', '');
    card.appendChild(status);

    if (handle && available) {
      var view = el('a', 'bg-card__view', opts.viewLabel || 'View product');
      view.href = '/products/' + handle;
      // Inside a <button>, so keep this click from also adding to the cart.
      view.addEventListener('click', function (event) {
        event.stopPropagation();
      });
      card.appendChild(view);
    }

    // Stock is only known to Shopify, not to the bundle API. Once the product
    // lookup (shared with the price fill, so no extra request) says every
    // variant is sold out, grey the card and stop it adding to the cart.
    if (handle && available) {
      productInStock(handle).then(function (inStock) {
        if (!inStock) markSoldOut(card, opts.soldOutLabel);
      }).catch(function (e) {
        // A 404 means the product is gone from the storefront; anything else
        // (network blip) leaves the card alone so a click can still retry.
        if (/404/.test(e && e.message)) markSoldOut(card, opts.unavailableLabel || 'Unavailable');
      });
    }

    if (pickable) {
      card.addEventListener('click', function (event) {
        event.preventDefault();
        if (card.classList.contains('bg-card--sold-out')) return;
        opts.onAdd(product, card, status);
      });

      // Resolve the variant before the click rather than during it.
      var prefetch = function () { firstAvailableVariant(handle).catch(function () {}); };
      card.addEventListener('pointerenter', prefetch, { once: true });
      card.addEventListener('focusin', prefetch, { once: true });
    }

    return card;
  }

  /*
    Card data rendered by the theme itself (templates/product.bundle-card.liquid):
    the price block and badges come from the same snippets as every other
    product card, so weight, compare-at price and the discount badge match.
  */
  var cardDataPromises = {};

  function themeCardData(handle) {
    if (cardDataPromises[handle]) return cardDataPromises[handle];
    cardDataPromises[handle] = fetch('/products/' + encodeURIComponent(handle) + '?view=bundle-card')
      .then(function (res) {
        if (!res.ok) throw new Error('product ' + res.status);
        return res.json();
      })
      .catch(function (e) {
        delete cardDataPromises[handle];
        throw e;
      });
    return cardDataPromises[handle];
  }

  /*
    A bundle product in the theme's own card design (snippets/product-item.liquid):
    image and title open the product, the round + button adds it to the cart.
    Once the product is in the cart the card gets the campaign border, a tick
    and an "Added · N" line, via the same paintCard() as the other rails.
  */
  function themeProductCard(product, options) {
    var opts = options || {};
    var handle = product.handle || '';
    var url = handle ? '/products/' + handle : null;

    var card = el('div', 'product-item card my-class bg-tcard');
    if (handle) card.dataset.handle = handle;
    if (product.available === false) card.classList.add('bg-card--unavailable');
    if (opts.isReward) card.classList.add('bg-card--reward');

    var imageLink = el(url ? 'a' : 'div', 'card__image product-item__image');
    if (url) imageLink.href = url;
    imageLink.style.paddingTop = '100%';
    var figure = el('figure', 'lazy-image product-item__image-figure product-item__image-figure--primary lazy-image--background lazy-image--fit lazyloaded');
    if (product.image) {
      var img = document.createElement('img');
      img.className = 'img';
      img.src = sized(product.image, 480);
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      figure.appendChild(img);
    }
    imageLink.appendChild(figure);
    card.appendChild(imageLink);

    var text = el('div', 'card__text product-item__text gutter--regular spacing--xlarge remove-empty-space text-align--left');

    var priceWrap = el('div', 'product-item__price text-size--large equalize-white-space');
    var priceInner = el('div', 'remove-line-height-space');
    priceWrap.appendChild(priceInner);
    text.appendChild(priceWrap);

    var titleLink = el(url ? 'a' : 'div', 'product-item__title');
    if (url) {
      titleLink.href = url;
      titleLink.title = product.title || '';
    }
    var titleInner = el('div', 'remove-line-height-space--small');
    var titleText = product.title || '';
    if (titleText.length > 45) titleText = titleText.slice(0, 42) + '...';
    titleInner.appendChild(el('span', 'text-animation--underline text-size--large text-line-height--small text-weight--bold', titleText));
    titleLink.appendChild(titleInner);
    text.appendChild(titleLink);

    // "Adding…" / "Added · N" feedback slot, painted by paintCard().
    var status = el('span', 'bg-card__status', '');
    text.appendChild(status);
    card.appendChild(text);

    var buy = el('div', 'product-item__text gutter--regular spacing--xlarge remove-empty-space text-align--left');
    buy.style.paddingTop = '0';
    card.appendChild(buy);

    card.appendChild(el('span', 'bg-card__tick', '✓'));

    var addButton = null;
    var showAddButton = function () {
      if (addButton || !opts.onAdd || !handle) return;
      var quickBuy = el('div', 'product-item__quick-buy');
      var holder = el('div', 'quick-add-to-cart main-div-plus');
      addButton = el('button', 'button button--outline button--regular button--fullwidth rounded-btn color-yellow-2 bg-tcard__add');
      addButton.type = 'button';
      addButton.setAttribute('aria-label', (opts.addLabel || 'Add') + ' ' + (product.title || ''));
      addButton.appendChild(el('span', 'button__text span-plus', '+'));
      addButton.addEventListener('click', function (event) {
        event.preventDefault();
        if (card.classList.contains('bg-card--sold-out')) return;
        opts.onAdd(product, card, status);
      });
      // Resolve the variant before the click rather than during it.
      var prefetch = function () { firstAvailableVariant(handle).catch(function () {}); };
      addButton.addEventListener('pointerenter', prefetch, { once: true });
      addButton.addEventListener('focusin', prefetch, { once: true });
      holder.appendChild(addButton);
      quickBuy.appendChild(holder);
      buy.appendChild(quickBuy);
    };

    var fallbackPrice = function () {
      var price = el('div', 'product-price');
      var original = el('span', 'product-price--original', '');
      price.appendChild(original);
      priceInner.appendChild(price);
      fillShopifyPrice(handle, original, product.price);
    };

    if (!handle) {
      fallbackPrice();
      return card;
    }

    themeCardData(handle).then(function (data) {
      priceInner.innerHTML = data.priceHtml || '';
      if (data.badgesHtml) card.insertAdjacentHTML('beforeend', data.badgesHtml);
      if (data.image && !product.image) {
        var dataImg = document.createElement('img');
        dataImg.className = 'img';
        dataImg.src = data.image;
        dataImg.alt = data.title || '';
        figure.appendChild(dataImg);
      }
      if (data.available && product.available !== false) {
        showAddButton();
      } else {
        card.classList.add('bg-card--sold-out');
      }
    }).catch(function (e) {
      if (/404/.test(e && e.message)) {
        card.classList.add('bg-card--sold-out', 'bg-card--unavailable');
        return;
      }
      // Network blip: keep the card usable with the plain Shopify price.
      fallbackPrice();
      if (product.available !== false) showAddButton();
    });

    return card;
  }

  /*
    Sold-out look for one card: greyed, flagged, and no longer an add control.
    The card is not `disabled` because that would also swallow clicks on the
    "View product" link inside it — the click handler checks the class instead.
  */
  function markSoldOut(card, label) {
    if (card.classList.contains('bg-card--sold-out')) return;
    card.classList.add('bg-card--sold-out', 'bg-card--unavailable');
    card.classList.remove('bg-card--pickable');
    card.setAttribute('aria-disabled', 'true');
    card.removeAttribute('aria-label');

    var hint = card.querySelector('.bg-card__hint');
    if (hint) hint.remove();

    var image = card.querySelector('.bg-card__image');
    if (image && !card.querySelector('.bg-card__flag')) {
      image.appendChild(el('span', 'bg-card__flag bg-card__flag--sold-out', label || 'Sold out'));
    }
  }

  /* Mark the cards for products already in the cart, and show the count. */
  function markCardsInCart(root, quantities, addLabel) {
    root.querySelectorAll('[data-handle]').forEach(function (card) {
      var quantity = quantities[card.dataset.handle] || 0;
      card.dataset.qty = String(quantity);
      paintCard(card, quantity, addLabel);
    });
  }

  /* The in-cart look for one card. */
  function paintCard(card, quantity, addLabel) {
    card.classList.toggle('bg-card--in-cart', quantity > 0);

    var status = card.querySelector('.bg-card__status');
    if (!status || card.classList.contains('working')) return;
    status.textContent = quantity > 0 ? 'Added · ' + quantity : '';

    var hint = card.querySelector('.bg-card__hint');
    if (hint) hint.textContent = quantity > 0 ? (addLabel || 'Add') + ' another' : (addLabel || 'Add');
  }

  /*
    Add one of a product to the cart from a rail.

    The API leaves variantId empty, so the variant is resolved from the
    storefront. After the add, the theme's own cart markup is re-rendered and
    lg:cart:updated fires, which is what makes the progress lines on this page
    and the cart strip recalculate.
  */
  /*
    Add one of a product to the cart from a rail.

    Ordered around this store's latency. /products/<handle>.js and /cart.js each
    cost roughly a second here, so the click path avoids both: the variant comes
    from the prefetch cache, and the card is repainted by bumping the count it
    already knows rather than re-reading the cart. That leaves one unavoidable
    request — /cart/add.js — between the click and the confirmation.

    Re-rendering the theme's cart markup and announcing lg:cart:updated happen
    afterwards, deliberately unawaited: they drive the drawer and the progress
    lines, and neither should hold up the button the shopper is looking at.
  */
  async function addProductToCart(product, status, context) {
    var card = context && context.card;
    if (!product.handle || (card && card.classList.contains('working'))) return false;

    if (card) card.classList.add('working');
    if (status) status.textContent = 'Adding…';

    try {
      var variantId = await firstAvailableVariant(product.handle);
      if (!variantId) {
        if (card) {
          card.classList.remove('working');
          markSoldOut(card, context && context.soldOutLabel);
        }
        if (status) status.textContent = '';
        return false;
      }

      await addToCart(variantId, 1);
      if (card) card.classList.remove('working');

      if (card) {
        var quantity = (parseInt(card.dataset.qty, 10) || 0) + 1;
        card.dataset.qty = String(quantity);
        paintCard(card, quantity, context.addLabel);
      }

      // Background: the drawer and the progress lines catch up on their own.
      refreshCartUI().then(function () {
        document.dispatchEvent(new CustomEvent('lg:cart:updated'));
      });

      return true;
    } catch (e) {
      console.warn('[bundle-gift] add to cart', e);
      if (card) card.classList.remove('working');
      if (status) status.textContent = 'Try again';
      return false;
    }
  }

  /* handle -> quantity, for reflecting cart state on the cards. */
  function cartQuantities() {
    return cartSnapshot().then(function (snapshot) {
      var map = {};
      snapshot.items.forEach(function (item) {
        map[item.handle] = item.quantity;
      });
      return map;
    });
  }

  window.LGBundleGift = {
    DEFAULT_BASE: DEFAULT_BASE,
    STORAGE: STORAGE,
    apiGet: apiGet,
    apiPost: apiPost,
    cartItems: cartItems,
    cartSnapshot: cartSnapshot,
    firstAvailableVariant: firstAvailableVariant,
    productInStock: productInStock,
    addToCart: addToCart,
    refreshCartUI: refreshCartUI,
    installCartWatcher: installCartWatcher,
    readClaim: readClaim,
    writeClaim: writeClaim,
    clearClaim: clearClaim,
    readLocal: readLocal,
    writeLocal: writeLocal,
    deviceId: deviceId,
    money: money,
    toNumber: toNumber,
    sized: sized,
    el: el,
    template: template,
    productCard: productCard,
    themeProductCard: themeProductCard,
    markCardsInCart: markCardsInCart,
    paintCard: paintCard,
    addProductToCart: addProductToCart,
    cartQuantities: cartQuantities
  };

  /* ==================================================== <bundle-gift-family> */

  /*
    Product page. GET /family tells us whether this product belongs to a bundle
    and who its siblings are. Most products are in no bundle at all, so a
    found:false response is the common case and renders nothing.
  */
  class BundleGiftFamily extends HTMLElement {

    connectedCallback() {
      this.base = (this.dataset.apiBase || DEFAULT_BASE).replace(/\/+$/, '');
      this.handle = this.dataset.handle || '';
      this.rail = this.querySelector('[data-bg-rail]');
      this.badgeSlot = this.querySelector('[data-bg-badge]');
      this.titleSlot = this.querySelector('[data-bg-title]');
      this.descriptionSlot = this.querySelector('[data-bg-description]');
      this.progressSlot = this.querySelector('[data-bg-progress]');

      if (!this.rail || !this.handle) return;

      installCartWatcher();
      this.load();

      this.addLabel = this.dataset.addLabel || 'Add';

      this.onCartUpdate = function () {
        this.refreshProgress();
        this.syncCards();
      }.bind(this);
      document.addEventListener('lg:cart:updated', this.onCartUpdate);
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener('lg:cart:updated', this.onCartUpdate);
    }

    async load() {
      try {
        var payload = await this.fetchFamily();
        // found:false is normal — most products are in no bundle.
        if (!payload || payload.success === false || payload.found !== true) return;

        var entries = Array.isArray(payload.data) ? payload.data : [];
        // Results are ordered by campaign priority; a product in two bundles is
        // rare but possible, so take the first.
        var entry = entries[0];
        if (!entry || !entry.group) return;

        this.entry = entry;
        this.render(entry);
        this.refreshProgress();
      } catch (e) {
        console.warn('[bundle-gift-family]', e);
      }
    }

    async fetchFamily() {
      var key = STORAGE.family + this.handle;
      try {
        var raw = window.sessionStorage.getItem(key);
        if (raw) return JSON.parse(raw);
      } catch (e) { /* ignore */ }

      var payload = await apiGet(this.base, '/family', { handle: this.handle });
      try {
        window.sessionStorage.setItem(key, JSON.stringify(payload));
      } catch (e) { /* ignore */ }
      return payload;
    }

    render(entry) {
      var group = entry.group || {};
      var display = group.display || {};
      var siblings = Array.isArray(entry.siblings) ? entry.siblings : [];
      if (!siblings.length) return;

      if (this.badgeSlot) {
        if (display.badgeText) this.badgeSlot.textContent = display.badgeText;
        else this.badgeSlot.remove();
      }
      if (this.titleSlot && !this.titleSlot.textContent.trim()) {
        this.titleSlot.textContent = group.name || '';
      }
      if (this.descriptionSlot) {
        if (group.description) this.descriptionSlot.textContent = group.description;
        else this.descriptionSlot.remove();
      }

      var self = this;
      var frag = document.createDocumentFragment();

      siblings.forEach(function (sibling) {
        // available:false means the product was unpublished or deleted in
        // Shopify since the bundle was built — grey it out and don't link it,
        // otherwise the card points at a 404.
        frag.appendChild(productCard(sibling, {
          isReward: sibling.isReward === true,
          unavailableLabel: self.dataset.unavailableLabel,
          soldOutLabel: self.dataset.soldOutLabel,
          rewardLabel: self.dataset.rewardLabel,
          addLabel: self.addLabel,
          // Adding from the card is the point of the rail: completing a set
          // should not mean opening five product pages.
          onAdd: function (product, card, status) {
            addProductToCart(product, status, { root: self, card: card, addLabel: self.addLabel, soldOutLabel: self.dataset.soldOutLabel });
          }
        }));
      });

      this.rail.innerHTML = '';
      this.rail.appendChild(frag);
      this.removeAttribute('hidden');
      this.syncCards();
    }

    /* Reflect what is already in the cart on the cards. */
    async syncCards() {
      try {
        markCardsInCart(this, await cartQuantities(), this.addLabel);
      } catch (e) { /* cosmetic only */ }
    }

    /* Live "add N more" line, driven by the real cart. */
    async refreshProgress() {
      if (!this.entry || !this.progressSlot) return;
      var group = this.entry.group || {};

      try {
        var items = await cartItems();
        if (!items.length) {
          // Emptying the cart has to retract the claim link too, not just reset
          // the copy — otherwise a link offering a gift outlives the cart that
          // earned it.
          this.progressSlot.classList.remove('bg-progress--unlocked');
          this.progressSlot.textContent = this.lockedCopy(group.requiredQuantity || this.entry.requiredQuantity || 0);
          this.showClaimLink(false);
          return;
        }

        var result = await apiPost(this.base, '/check', {
          groupId: group.id,
          email: this.dataset.customerEmail || readLocal(STORAGE.email) || '',
          items: items
        });
        if (!result || result.success === false) return;

        var progress = result.progress || {};
        // Once the gift is claimed /check stops reporting eligible, but the set
        // is still complete; "Add 0 more" would read as a broken counter.
        if (result.eligible === true || setComplete(progress)) {
          this.progressSlot.textContent = (group.display && group.display.unlockedText) || 'Gift unlocked!';
          this.progressSlot.classList.add('bg-progress--unlocked');
          this.showClaimLink(result.eligible === true);
        } else {
          this.progressSlot.classList.remove('bg-progress--unlocked');
          this.progressSlot.textContent = this.lockedCopy(progress.remainingToUnlock);
          this.showClaimLink(false);
        }
      } catch (e) {
        // Progress is a nicety; the rail itself stays.
      }
    }

    /*
      Once the set is complete the shopper has to reach the cart to claim, so
      say so on the page they are already on rather than hoping they open the
      drawer.
    */
    showClaimLink(show) {
      if (!show) {
        if (this.claimLink) {
          this.claimLink.remove();
          this.claimLink = null;
        }
        return;
      }
      if (this.claimLink) return;

      var link = el('a', 'bg-family__claim button button--solid button--regular',
        this.dataset.claimLinkLabel || 'Go to cart and claim your gift');
      link.href = (window.KROWN && KROWN.settings && KROWN.settings.routes && KROWN.settings.routes.cart_url) || '/cart';
      this.progressSlot.insertAdjacentElement('afterend', link);
      this.claimLink = link;
    }

    lockedCopy(remaining) {
      var group = (this.entry && this.entry.group) || {};
      var display = group.display || {};
      var count = remaining == null ? (group.requiredQuantity || 0) : remaining;
      if (display.lockedText) return template(display.lockedText, { remaining: count });
      return 'Add ' + count + ' more to unlock your gift';
    }
  }

  /* ==================================================== <bundle-gift-offers> */

  /*
    Home page. GET /active lists every live bundle. The payload is the same
    group shape used elsewhere; entries may arrive either as a bare group or
    wrapped as { group: {...} }, so both are accepted.
  */
  class BundleGiftOffers extends HTMLElement {

    connectedCallback() {
      this.base = (this.dataset.apiBase || DEFAULT_BASE).replace(/\/+$/, '');
      this.rail = this.querySelector('[data-bg-rail]');
      this.limit = parseInt(this.dataset.limit, 10) || 8;
      this.addLabel = this.dataset.addLabel || 'Add';
      this.showProducts = this.dataset.showProducts !== 'false';
      this.entries = [];
      if (!this.rail) return;

      installCartWatcher();
      this.onCartUpdate = this.debounce(this.refreshAll.bind(this), 250);
      document.addEventListener('lg:cart:updated', this.onCartUpdate);

      this.load();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener('lg:cart:updated', this.onCartUpdate);
    }

    debounce(fn, wait) {
      var timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    async load() {
      try {
        var payload = await apiGet(this.base, '/active', {});
        if (!payload || payload.success === false) return;

        var entries = Array.isArray(payload.data) ? payload.data : [];
        var groups = entries.map(function (entry) {
          // /active returns bare groups whose product list is called
          // `products`; /family wraps them as { group, family }. Accept both.
          if (entry && entry.group) {
            return { group: entry.group, family: entry.family || entry.products || [] };
          }
          return { group: entry, family: (entry && entry.products) || [] };
        }).filter(function (entry) {
          return entry.group && entry.group.id;
        });

        if (!groups.length) return;
        this.render(groups.slice(0, this.limit));
      } catch (e) {
        console.warn('[bundle-gift-offers]', e);
      }
    }

    render(entries) {
      var self = this;
      var frag = document.createDocumentFragment();
      var rendered = 0;

      entries.forEach(function (entry) {
        var group = entry.group;
        var display = group.display || {};
        var family = Array.isArray(entry.family) ? entry.family : [];

        var block = el('div', 'bg-offer element--border-radius');
        block.dataset.groupId = group.id || '';

        // A group has no url of its own, so the artwork opens the first
        // available product of the family. With no family in the payload it
        // stays inert rather than becoming a dead link.
        var firstAvailable = family.filter(function (p) {
          return p && p.handle && p.available !== false;
        })[0];

        var image = display.image || (firstAvailable && firstAvailable.image) || '';
        if (image) {
          var head = document.createElement(firstAvailable ? 'a' : 'div');
          head.className = 'bg-offer__image';
          if (firstAvailable) head.href = '/products/' + firstAvailable.handle;

          var img = document.createElement('img');
          img.src = sized(image, 900);
          img.alt = group.name || '';
          img.loading = 'lazy';
          img.decoding = 'async';
          head.appendChild(img);
          block.appendChild(head);
        }

        var text = el('div', 'bg-offer__text');
        if (display.badgeText) text.appendChild(el('span', 'bg-offer__badge', display.badgeText));
        if (group.name) text.appendChild(el('span', 'bg-offer__title', group.name));
        if (group.description) text.appendChild(el('span', 'bg-offer__description', group.description));
        text.appendChild(el('p', 'bg-offer__progress', ''));
        block.appendChild(text);

        // The products, addable straight from here — the whole point is that a
        // shopper can build the set without leaving the home page.
        if (self.showProducts && family.length) {
          var rail = el('div', 'bg-rail bg-offer__products');
          rail.setAttribute('role', 'list');

          family.forEach(function (product) {
            rail.appendChild(themeProductCard(product, {
              isReward: product.isReward === true,
              soldOutLabel: self.dataset.soldOutLabel,
              addLabel: self.addLabel,
              onAdd: function (item, card, status) {
                addProductToCart(item, status, { root: self, card: card, addLabel: self.addLabel, soldOutLabel: self.dataset.soldOutLabel });
              }
            }));
          });

          block.appendChild(rail);
        }

        frag.appendChild(block);
        rendered += 1;
      });

      if (!rendered) return;

      this.rail.innerHTML = '';
      this.rail.appendChild(frag);
      this.classList.toggle('bundle-gift-offers--single', rendered === 1);
      this.removeAttribute('hidden');

      this.entries = entries;
      this.refreshAll();
    }

    /* Cart state on the cards, plus an "add N more" line per bundle. */
    async refreshAll() {
      if (!this.entries.length) return;

      try {
        markCardsInCart(this, await cartQuantities(), this.addLabel);
      } catch (e) { /* cosmetic only */ }

      var snapshot;
      try {
        snapshot = await cartSnapshot();
      } catch (e) {
        return;
      }

      var self = this;
      this.entries.forEach(function (entry) {
        self.refreshProgress(entry, snapshot.items);
      });
    }

    async refreshProgress(entry, items) {
      var group = entry.group || {};
      var block = this.querySelector('[data-group-id="' + group.id + '"]');
      if (!block) return;

      var slot = block.querySelector('.bg-offer__progress');
      if (!slot) return;

      var required = group.requiredQuantity || 0;
      var display = group.display || {};

      if (!items.length) {
        slot.classList.remove('bg-offer__progress--unlocked');
        slot.textContent = display.lockedText
          ? template(display.lockedText, { remaining: required })
          : 'Add ' + required + ' different products to unlock your gift';
        return;
      }

      try {
        var result = await apiPost(this.base, '/check', {
          groupId: group.id,
          email: this.dataset.customerEmail || readLocal(STORAGE.email) || '',
          items: items
        });
        if (!result || result.success === false) return;

        var progress = result.progress || {};
        if (result.eligible === true || setComplete(progress)) {
          slot.classList.add('bg-offer__progress--unlocked');
          slot.textContent = display.unlockedText || 'Gift unlocked! Open your cart to claim it';
        } else {
          slot.classList.remove('bg-offer__progress--unlocked');
          var remaining = progress.remainingToUnlock != null ? progress.remainingToUnlock : required;
          slot.textContent = display.lockedText
            ? template(display.lockedText, { remaining: remaining })
            : 'Add ' + remaining + ' more to unlock your gift';
        }
      } catch (e) {
        // Progress is a nicety; the rail itself stays.
      }
    }
  }

  /* ====================================================== <bundle-gift-cart> */

  /*
    Cart drawer and cart page. POST /check on every cart change (it issues
    nothing, so it is safe to call freely), then POST /claim once, on an
    explicit shopper action.

    The reward is locked the moment the code is issued: a second /claim returns
    the same code with the original reward, whatever rewardProductId is sent.
    So the chooser is confirmed before /claim is called, never after.
  */
  class BundleGiftCart extends HTMLElement {

    connectedCallback() {
      this.base = (this.dataset.apiBase || DEFAULT_BASE).replace(/\/+$/, '');
      this.body = this.querySelector('[data-bg-body]');
      if (!this.body) return;

      this.customerEmail = this.dataset.customerEmail || '';
      this.selectedReward = null;
      this.busy = false;

      installCartWatcher();

      this.onCartUpdate = this.debounce(this.refresh.bind(this), 250);
      document.addEventListener('lg:cart:updated', this.onCartUpdate);

      this.refresh();
    }

    disconnectedCallback() {
      if (this.onCartUpdate) document.removeEventListener('lg:cart:updated', this.onCartUpdate);
      this.stopExpiryWatch();
    }

    debounce(fn, wait) {
      var timer;
      return function () {
        clearTimeout(timer);
        timer = setTimeout(fn, wait);
      };
    }

    email() {
      return this.customerEmail || readLocal(STORAGE.email) || '';
    }

    async refresh() {
      // A live code outranks everything: once issued it is what the shopper
      // needs to see, even though /check now reports alreadyClaimed.
      var claim = readClaim();

      try {
        var snapshot = await cartSnapshot();
        this.cartTotal = snapshot.total;

        if (!snapshot.items.length) {
          if (!claim) return this.hide();
          this.check = null;
          return this.renderClaimed(claim);
        }

        var result = await apiPost(this.base, '/check', {
          email: this.email(),
          items: snapshot.items
        });

        if (!result || result.success === false || !result.group) {
          if (claim) return this.renderClaimed(claim);
          return this.hide();
        }

        this.check = result;

        if (claim && claim.groupId === (result.group && result.group.id)) {
          this.renderClaimed(claim, result);
          return this.widen(claim, result);
        }
        if (result.alreadyClaimed === true) return this.renderAlreadyClaimed(result);
        if (result.eligible === true) return this.renderEligible(result);
        return this.renderProgress(result);
      } catch (e) {
        // Fail open: on a network error the strip disappears rather than
        // showing a broken promise.
        if (claim) return this.renderClaimed(claim);
        this.hide();
      }
    }

    hide() {
      this.stopExpiryWatch();
      this.body.innerHTML = '';
      this.setAttribute('hidden', '');
    }

    show() {
      this.removeAttribute('hidden');
    }

    /* ------------------------------------------------------------- states */

    renderProgress(result) {
      var group = result.group || {};
      var display = group.display || {};
      var progress = result.progress || {};
      var remaining = progress.remainingToUnlock != null ? progress.remainingToUnlock : 0;

      // Nothing from this bundle in the cart yet (0 / N): the offer isn't
      // relevant to this basket, so keep the strip out of the way.
      if (toNumber(progress.qualifyingCount) <= 0) return this.hide();

      this.stopExpiryWatch();
      this.body.innerHTML = '';

      this.body.appendChild(this.header(group));
      this.body.appendChild(el('p', 'bg-cart__message',
        display.lockedText ? template(display.lockedText, { remaining: remaining })
                           : 'Add ' + remaining + ' more to unlock your gift'));

      this.body.appendChild(this.progressBar(progress));

      // countDistinctProducts is the usual setting: 4 of the same flavour does
      // not unlock, so the copy has to say so.
      if (result.countDistinctProducts !== false && group.countDistinctProducts !== false) {
        this.body.appendChild(el('p', 'bg-cart__hint', 'Each item must be a different product from this group.'));
      }

      this.show();
    }

    renderEligible(result) {
      var group = result.group || {};
      var display = group.display || {};
      var candidates = Array.isArray(result.rewardCandidates) ? result.rewardCandidates : [];
      var rewards = Array.isArray(result.rewardProducts) ? result.rewardProducts : [];

      this.stopExpiryWatch();
      this.body.innerHTML = '';

      this.body.appendChild(this.header(group));
      this.body.appendChild(el('p', 'bg-cart__message bg-cart__message--unlocked',
        display.unlockedText || 'Gift unlocked!'));
      this.appendUnitsNote(rewardUnits(result));

      /*
        Warn before the claim, not after. A group can carry a flat minimum
        order that is worth more than the qualifying items (the Mak Bar group
        unlocks at three bars worth about $6 but its codes require a $10
        order). The claim is one per customer and the code expires in minutes,
        so letting a shopper spend it on an order that cannot meet the
        threshold burns it for good.
      */
      var minimum = toNumber(result.minOrderAmount);
      var total = toNumber(this.cartTotal);
      if (minimum > 0 && total > 0 && total < minimum) {
        this.body.appendChild(el('p', 'bg-cart__warning',
          'Your gift code will need an order of at least ' + money(minimum) +
          '. Add ' + money(minimum - total) + ' more before you claim it, or it ' +
          'will be refused at checkout.'));
      }

      // With more than one candidate the shopper picks which product the code
      // will discount; with one the lists are identical and no selector is
      // needed.
      if (candidates.length > 1) {
        this.body.appendChild(el('p', 'bg-cart__hint', 'Pick your gift:'));
        this.body.appendChild(this.rewardChooser(candidates));
      } else if (rewards.length) {
        this.selectedReward = rewards[0].productId || '';
        this.body.appendChild(this.rewardSummary(rewards[0]));
      }

      var actions = el('div', 'bg-cart__actions');

      if (!this.email()) {
        var field = el('div', 'bg-cart__email');
        var input = document.createElement('input');
        input.type = 'email';
        input.className = 'bg-cart__email-input';
        input.placeholder = this.dataset.emailPlaceholder || 'your@email.com';
        input.autocomplete = 'email';
        input.setAttribute('aria-label', this.dataset.emailLabel || 'Email for your gift code');
        field.appendChild(el('label', 'bg-cart__email-label', this.dataset.emailLabel || 'Enter your email to get the code'));
        field.appendChild(input);
        actions.appendChild(field);
        this.emailInput = input;
      } else {
        this.emailInput = null;
      }

      var button = el('button', 'bg-cart__button bg-cart__button--claim button button--solid button--fullwidth');
      button.appendChild(el('span', 'bg-cart__button-icon', '\uD83C\uDF81'));
      button.appendChild(el('span', 'bg-cart__button-label', this.dataset.claimLabel || 'Claim my gift'));
      button.type = 'button';
      button.addEventListener('click', this.claim.bind(this, result));
      actions.appendChild(button);

      this.claimButton = button;
      this.body.appendChild(actions);
      this.body.appendChild(el('p', 'bg-cart__error', ''));
      this.show();
    }

    renderAlreadyClaimed(result) {
      var group = result.group || {};
      this.stopExpiryWatch();
      this.body.innerHTML = '';
      this.body.appendChild(this.header(group));
      this.body.appendChild(el('p', 'bg-cart__message',
        this.dataset.alreadyClaimedLabel || 'You have already used this gift.'));
      this.show();
    }

    renderClaimed(claim, check) {
      this.stopExpiryWatch();
      this.body.innerHTML = '';

      this.body.appendChild(this.header({ name: claim.groupName, display: {} }));
      this.body.appendChild(el('p', 'bg-cart__message bg-cart__message--unlocked',
        this.dataset.claimedLabel || 'Your gift code is ready'));

      var codeRow = el('div', 'bg-cart__code');
      codeRow.appendChild(el('span', 'bg-cart__code-value', claim.code));

      var copy = el('button', 'bg-cart__copy', this.dataset.copyLabel || 'Copy');
      copy.type = 'button';
      copy.addEventListener('click', function () {
        if (navigator.clipboard) {
          navigator.clipboard.writeText(claim.code).then(function () {
            copy.textContent = 'Copied';
          }).catch(function () { /* ignore */ });
        }
      });
      codeRow.appendChild(copy);
      this.body.appendChild(codeRow);

      var reward = (claim.rewardProducts || [])[0];
      this.appendUnitsNote(rewardUnits(claim));
      if (reward && reward.title) {
        this.body.appendChild(el('p', 'bg-cart__hint',
          'Applies to ' + reward.title + ' only.'));
      }

      this.watchExpiry(claim);

      /*
        The code carries a minimum order, and Shopify measures that against the
        whole cart subtotal — not against the bundle's items. The two are not
        the same number: a group can be configured with a flat minimum (the Mak
        Bar group uses a fixed $10) that is higher than the qualifying items
        are worth, so the shopper needs other items in the cart too.

        Comparing against the cart total rather than qualifyingSubtotal is what
        makes this warning accurate in both directions — it fires when the cart
        genuinely cannot meet the threshold, and stays quiet when other items
        already carry it.
      */
      var minimum = toNumber(claim.minOrderAmount);
      var total = toNumber(this.cartTotal);
      if (minimum > 0 && total > 0 && total < minimum) {
        this.body.appendChild(el('p', 'bg-cart__warning',
          'This code needs an order of at least ' + money(minimum) + '. Your cart is ' +
          money(total) + ' — add ' + money(minimum - total) +
          ' more or it will be refused at checkout.'));
      }

      var checkout = el('a', 'bg-cart__button bg-cart__button--claim button button--solid button--fullwidth');
      checkout.appendChild(el('span', 'bg-cart__button-icon', '\uD83C\uDF81'));
      checkout.appendChild(el('span', 'bg-cart__button-label',
        this.dataset.checkoutLabel || 'Checkout with my gift'));
      checkout.href = '/checkout?discount=' + encodeURIComponent(claim.code);
      this.body.appendChild(checkout);

      this.show();
    }

    /* ------------------------------------------------------------- pieces */

    header(group) {
      var wrap = el('div', 'bg-cart__header');
      var display = (group && group.display) || {};
      if (display.badgeText) wrap.appendChild(el('span', 'bg-cart__badge', display.badgeText));
      if (group && group.name) wrap.appendChild(el('span', 'bg-cart__name', group.name));
      return wrap;
    }

    progressBar(progress) {
      var required = toNumber(progress.requiredQuantity);
      var current = toNumber(progress.qualifyingCount);
      var percent = required > 0 ? Math.min(100, Math.round((current / required) * 100)) : 0;

      var wrap = el('div', 'bg-cart__progress');
      var track = el('span', 'bg-cart__progress-track');
      var fill = el('span', 'bg-cart__progress-fill');
      fill.style.width = percent + '%';
      track.appendChild(fill);
      wrap.appendChild(track);
      wrap.appendChild(el('span', 'bg-cart__progress-count', current + ' / ' + required));
      return wrap;
    }

    rewardChooser(candidates) {
      var list = el('div', 'bg-cart__rewards');
      var self = this;

      candidates.forEach(function (candidate, index) {
        var id = 'bg-reward-' + (candidate.productId || index);
        var option = el('label', 'bg-cart__reward');
        var input = document.createElement('input');
        input.type = 'radio';
        input.name = 'bg-reward-' + self.dataset.instance;
        input.value = candidate.productId || '';
        input.id = id;
        if (index === 0) {
          input.checked = true;
          self.selectedReward = candidate.productId || '';
        }
        input.addEventListener('change', function () {
          self.selectedReward = input.value;
        });

        option.appendChild(input);
        if (candidate.image) {
          var img = document.createElement('img');
          img.src = sized(candidate.image, 120);
          img.alt = '';
          img.loading = 'lazy';
          option.appendChild(img);
        }
        option.appendChild(el('span', 'bg-cart__reward-title', candidate.title || ''));
        var price = el('span', 'bg-cart__reward-price', '');
        option.appendChild(price);
        fillShopifyPrice(candidate.handle, price, candidate.price);
        list.appendChild(option);
      });

      return list;
    }

    rewardSummary(reward) {
      var wrap = el('div', 'bg-cart__reward-summary');
      if (reward.image) {
        var img = document.createElement('img');
        img.src = sized(reward.image, 120);
        img.alt = '';
        img.loading = 'lazy';
        wrap.appendChild(img);
      }
      wrap.appendChild(el('span', 'bg-cart__reward-title', reward.title || ''));
      return wrap;
    }

    /* ------------------------------------------------------------- claim */

    /* `held` is the live claim when this call widens it rather than issuing
       a new one. */
    async claim(result, held) {
      if (this.busy) return;

      var email = this.email();
      if (!email && this.emailInput) {
        email = (this.emailInput.value || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.emailInput.focus();
          return this.error('Enter a valid email address to receive your code.');
        }
        writeLocal(STORAGE.email, email);
      }
      if (!email) return this.error('An email address is required to claim this gift.');

      this.busy = true;
      if (this.claimButton) this.claimButton.classList.add('working');
      this.error('');

      try {
        var items = await cartItems();
        var group = result.group || {};

        var response = await apiPost(this.base, '/claim', {
          groupId: group.id,
          email: email,
          deviceId: deviceId(),
          // The reward is locked once the code exists; send the held one so
          // the request says what it means.
          rewardProductId: held
            ? (((held.rewardProducts || [])[0] || {}).productId || '')
            : (this.selectedReward || ''),
          items: items
        });

        // Business failures arrive as HTTP 200 with success:false.
        if (!response || response.success === false) {
          this.busy = false;
          if (this.claimButton) this.claimButton.classList.remove('working');
          // A failed widen leaves the held code as it was; it still works for
          // the sets it already covers.
          if (held) {
            console.warn('[bundle-gift-cart] gift not widened', response && (response.error || response.message));
            return;
          }
          if (response && response.error === 'ALREADY_CLAIMED') return this.renderAlreadyClaimed(result);
          return this.error((response && response.message) || 'This gift could not be claimed right now.');
        }

        var claim = {
          code: response.code,
          groupId: response.groupId || group.id,
          groupName: response.groupName || group.name || '',
          gift: response.gift || result.gift || null,
          minOrderAmount: response.minOrderAmount,
          rewardProducts: response.rewardProducts || [],
          // Read from every response, not just the first: a repeat claim
          // widens the code and returns the new figure.
          rewardUnits: rewardUnits(response),
          bundleCount: toNumber(response.bundleCount),
          expiresAt: response.expiresAt || ''
        };
        writeClaim(claim);

        // Step 1 of applying the code: the reward product has to be in the cart
        // for the discount to have anything to discount.
        await this.addReward(claim);

        // The gift changed the cart total, and the minimum-order warning is
        // measured against it.
        try {
          this.cartTotal = (await cartSnapshot()).total;
        } catch (e) { /* keep the pre-claim figure */ }

        this.busy = false;
        if (this.claimButton) this.claimButton.classList.remove('working');
        this.renderClaimed(claim, this.check);
      } catch (e) {
        this.busy = false;
        if (this.claimButton) this.claimButton.classList.remove('working');
        this.error('Something went wrong. Please try again.');
      }
    }

    /*
      More sets added after the claim. Re-calling /claim widens the live code
      in place (same code, bigger rewardUnits), and addReward then tops the
      cart up to the new figure, so the extra gifts land in the basket on
      their own. Each code+size is tried once, so a refusal can't loop through
      the cart updates the top-up itself triggers.
    */
    widen(claim, check) {
      var available = rewardUnits(check);
      if (available <= rewardUnits(claim) || this.busy) return;

      var attempt = claim.code + ':' + available;
      if (this.widenAttempt === attempt) return;
      this.widenAttempt = attempt;

      return this.claim(check, claim);
    }

    /*
      The code discounts up to rewardUnits units, but Shopify can only
      discount units that are actually in the cart. So top the reward product
      up to rewardUnits: a shopper who already has some of it gets only the
      difference, and a repeat claim adds only what the wider code gained.
    */
    async addReward(claim) {
      var reward = (claim.rewardProducts || [])[0];
      if (!reward || !reward.handle) return;

      try {
        var snapshot = await cartSnapshot();
        var inCart = 0;
        snapshot.items.forEach(function (item) {
          if (item.handle === reward.handle) inCart = item.quantity;
        });
        var missing = rewardUnits(claim) - inCart;
        if (missing <= 0) return;

        var variantId = await firstAvailableVariant(reward.handle);
        if (!variantId) return;
        await addToCart(variantId, missing);
        await refreshCartUI();
      } catch (e) {
        // The shopper still has the code; they can add the product themselves.
        console.warn('[bundle-gift-cart] reward not added', e);
      }
    }

    /* "You unlocked 3 rewards". Silent for a single set, which reads exactly
       as it did before. */
    appendUnitsNote(units) {
      if (units > 1) {
        this.body.appendChild(el('p', 'bg-cart__hint', 'You unlocked ' + units + ' rewards'));
      }
    }

    error(message) {
      var slot = this.querySelector('.bg-cart__error');
      if (slot) slot.textContent = message || '';
    }

    /* ------------------------------------------------------------- expiry */

    /*
      The code really does die after about ten minutes, so the strip still has
      to notice — it just does it silently now. A ticking countdown was putting
      a deadline in front of the shopper at the exact moment they were deciding
      whether to check out, which is the last place a clock helps.

      So: no display, one timer set for the moment the code expires. It drops
      the held code and re-renders, and the fresh /check decides what the
      shopper sees next — the claim button again, or "already used" if the
      backend counts the expired claim against them. Same behaviour the
      countdown had when it reached zero; only the clock is gone.
    */
    watchExpiry(claim) {
      var self = this;
      var expiry = claim.expiresAt ? new Date(claim.expiresAt).getTime() : 0;

      this.stopExpiryWatch();
      if (!expiry) return;

      // Always through the timer, never inline: this runs part-way through
      // building the panel, and refreshing from there would rebuild the body
      // under the code still appending to it.
      this.expiryTimer = setTimeout(function () {
        self.expiryTimer = null;
        clearClaim();
        self.refresh();
      }, Math.max(0, expiry - Date.now()));
    }

    stopExpiryWatch() {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer);
        this.expiryTimer = null;
      }
    }
  }

  if (typeof customElements.get('bundle-gift-family') === 'undefined') {
    customElements.define('bundle-gift-family', BundleGiftFamily);
  }
  if (typeof customElements.get('bundle-gift-offers') === 'undefined') {
    customElements.define('bundle-gift-offers', BundleGiftOffers);
  }
  if (typeof customElements.get('bundle-gift-cart') === 'undefined') {
    customElements.define('bundle-gift-cart', BundleGiftCart);
  }
})();
